<template>
  <v-form
    lazy-validation
    v-model="valid"
    @submit.prevent="submit"
    ref="form"
    :disabled="isCompleted"
  >
      <v-container class="text-center">
        <h1>Batch Printing Verification</h1>
        <h3>This verification should be completed every day.
          It should be completed for the start up before printing and
          thereafter for every change that needs to be made.
          If changes are made and there is no form to accompany this change,
          The printing operator will be held responsible should the changes not
          meet the necessary criteria.
        </h3>
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-date"
          :label="$$t('date')"
          v-model="localBatchPrintingVerification.date"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-farm"
          :label="$$t('farm')"
          v-model="localBatchPrintingVerification.farm"
        />
        <v-select
          filled
          :items="canSizes"
          data-cy="FormBatchPrintingVerification-canSize"
          label="Can Size"
          v-model="localBatchPrintingVerification.canSize"
          item-text="size"
          item-value="size"
        />
        <v-select
          filled
          :items="brandingCodes"
          data-cy="FormBatchPrintingVerification-brandingCode"
          :label="$$t('brandingCode')"
          v-model="localBatchPrintingVerification.brandingCode"
          item-text="code"
          item-value="code"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-pieces"
          :label="$$t('pieces')"
          v-model="localBatchPrintingVerification.pieces"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-drainweightCans"
          :label="$$t('drainweightCans')"
          v-model="localBatchPrintingVerification.drainweightCans"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-bestBeforeDate"
          :label="$$t('bestBeforeDate')"
          v-model="localBatchPrintingVerification.bestBeforeDate"
        />
        <AdminTextField
          filled
          data-cy="FormBatchPrintingVerification-factoryCode"
          :label="$$t('factoryCode')"
          v-model="localBatchPrintingVerification.factoryCode"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-productionBatchCode"
          :label="$$t('productionBatchCode')"
          v-model="localBatchPrintingVerification.productionBatchCode"
        />
        <v-text-field
          filled
          data-cy="FormBatchPrintingVerification-farmCode"
          :label="$$t('farmCode')"
          v-model="localBatchPrintingVerification.farmCode"
        />
        <hr class="mb-5 mt-5" />
        <div :class="{ dimmed: localBatchPrintingVerification.status }">
          <SignVerification role="Process Controller" title="Requested By"
            v-model="localBatchPrintingVerification.requestedBySignature" />
          <v-btn block color="success" @click="handover('Ready to be verified')">Handover</v-btn>
        </div>
        <hr class="mb-5 mt-5" />
        <div :class="{ dimmed: localBatchPrintingVerification.status !== 'Ready to be verified' }">
          <SignVerification role="Production Manager" title="Verified By"
            v-model="localBatchPrintingVerification.verifiedBySignature" />
          <v-btn block color="success" @click="handover('Ready for NRCS')">Handover</v-btn>
        </div>
        <hr class="mb-5 mt-5" />
        <div :class="{ dimmed: localBatchPrintingVerification.status !== 'Ready for NRCS' }">
          <SignVerification role="Nrcs" title="NRCS Inspector"
            v-model="localBatchPrintingVerification.ncrsSignature"/>
          <v-row>
            <v-col cols="12" class="mb-10">
              <v-btn block color="success" @click="handover('Completed')">Completed</v-btn>
            </v-col>
          </v-row>
        </div>
        <hr class="mb-5 mt-5" />
        <div class="d-flex justify-end mb-6">
          <v-btn color="default" outlined class="ml-4 btn-cancel" @click="cancel"
            data-cy="FormBatchPrintingVerification-button-cancel">
            {{ $t('common.buttons.cancel') }}
          </v-btn>
          <v-btn color="primary" type="submit" class="ml-4" :loading="isSaving"
            data-cy="FormBatchPrintingVerification-button-save">
              {{ $t('common.buttons.save') }}
            </v-btn>
        </div>
      </v-container>
  </v-form>
</template>

<script>
import { mapState } from 'vuex';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import formChecklist from './formChecklist';
import AdminTextField from './AdminTextField.vue';
import SignVerification from './SignVerification.vue';

export default {
  name: 'FormBatchPrintingVerification',
  module: 'BatchPrintingVerification',
  components: {
    AdminTextField,
    SignVerification,
  },
  mixins: [formChecklist, loading],
  props: {
    /**
    * Set the text box value. Default: undefined,
    */
    BatchPrintingVerification: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      /**
      * Hold form validation state
      */
      valid: false,
      /**
      * Local value used for property localBatchPrintingVerification
      */
      localBatchPrintingVerification: {},
    };
  },
  computed: {
    ...mapState('brandingCodes', ['brandingCodes']),
    ...mapState('canSizes', ['canSizes']),
  },
  watch: {
    BatchPrintingVerification() {
      this.localBatchPrintingVerification = this.BatchPrintingVerification;
    },
  },
  created() {
    this.localBatchPrintingVerification = this.BatchPrintingVerification;
  },
  methods: {
    /** Submit Form data trigger
     * @event save
     * @type {object}
     */
    submit() {
      // validate
      if (this.$refs.form.validate()) {
        this.$emit('save', this.localBatchPrintingVerification);
        this.isSaving = true;
      }
    },
    handover(status) {
      this.localBatchPrintingVerification.status = status;
      this.$emit('save', this.localBatchPrintingVerification);
    },
    /** Cancel form trigger
     * @event cancel
     */
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<docs>
# FormBatchPrintingVerification example

'''js
<FormBatchPrintingVerification :BatchPrintingVerification="BatchPrintingVerification" />
'''
</docs>
